import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Componente de partículas flutuantes (brasas)
function FloatingParticles() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      duration: Math.random() * 10 + 5,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.8 + 0.2,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full bg-orange-400"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            animation: `float ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
            boxShadow: '0 0 6px 2px rgba(251, 146, 60, 0.6)',
          }}
        />
      ))}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.3; }
            25% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
            50% { transform: translateY(-10px) translateX(-5px); opacity: 0.5; }
            75% { transform: translateY(-30px) translateX(15px); opacity: 0.9; }
          }
        `}
      </style>
    </div>
  );
}

// Logo SVG do Hit Grill com chamas
function HitGrillLogo({ className = '' }) {
  return (
    <svg viewBox="0 0 200 180" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Chamas */}
      <g>
        {/* Chama central grande */}
        <path
          d="M100 10 Q85 50 90 80 Q75 60 80 100 Q65 80 75 120 Q60 100 70 140 L100 160 L130 140 Q140 100 135 120 Q145 80 125 100 Q130 60 110 80 Q115 50 100 10"
          fill="url(#flameGradient)"
        />
        {/* Chama esquerda */}
        <path
          d="M60 40 Q50 70 55 90 Q45 75 50 110 Q40 95 48 130 L65 145 L80 130 Q85 95 75 110 Q80 75 70 90 Q75 60 60 40"
          fill="url(#flameGradient)"
          opacity="0.9"
        />
        {/* Chama direita */}
        <path
          d="M140 40 Q150 70 145 90 Q155 75 150 110 Q160 95 152 130 L135 145 L120 130 Q115 95 125 110 Q120 75 130 90 Q125 60 140 40"
          fill="url(#flameGradient)"
          opacity="0.9"
        />
      </g>
      <defs>
        <linearGradient id="flameGradient" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#EA580C" />
          <stop offset="50%" stopColor="#F97316" />
          <stop offset="100%" stopColor="#FDBA74" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Login realizado com sucesso!');
      } else {
        await register({ email, password, name, role: 'GERENTE' });
        toast.success('Cadastro realizado com sucesso!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar operação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Lado Esquerdo - Branding Futurista */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 50%, #1a1a1a 100%)' }}>
        {/* Gradiente laranja central */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(249, 115, 22, 0.3) 0%, rgba(234, 88, 12, 0.1) 30%, transparent 60%)',
          }}
        />
        
        {/* Linha horizontal dourada */}
        <div 
          className="absolute left-0 right-0 h-px"
          style={{
            top: '55%',
            background: 'linear-gradient(90deg, transparent 0%, #F97316 20%, #FDBA74 50%, #F97316 80%, transparent 100%)',
            boxShadow: '0 0 20px 2px rgba(249, 115, 22, 0.5)',
          }}
        />
        
        {/* Partículas flutuantes */}
        <FloatingParticles />
        
        {/* Conteúdo central */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full px-12">
          {/* Logo com chamas */}
          <img src="/logo.png" alt="Hit Grill" className="w-64 h-auto mb-4" />
          
          {/* Texto HIT GRILL */}
          <h1 
            className="text-6xl font-bold text-white tracking-wider mb-6"
            style={{ 
              fontFamily: 'Arial Black, sans-serif',
              textShadow: '0 0 30px rgba(249, 115, 22, 0.5)',
              letterSpacing: '0.15em'
            }}
          >
            HIT GRILL
          </h1>
          
          {/* Descrição */}
          <p className="text-gray-400 text-lg text-center mb-4">
            Sistema completo de gestão para restaurantes<br />e delivery
          </p>
          
          {/* ASSADOS NA BRASA */}
          <p 
            className="text-orange-500 text-sm tracking-[0.3em] font-semibold"
            style={{ letterSpacing: '0.4em' }}
          >
            ASSADOS NA BRASA
          </p>
        </div>
      </div>

      {/* Lado Direito - Formulário */}
      <div className="w-full lg:w-2/5 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {isLogin ? 'Bem-vindo de volta' : 'Criar conta'}
          </h2>
          <p className="text-gray-500 mb-8">
            {isLogin ? 'Entre com suas credenciais para acessar o sistema' : 'Preencha os dados para criar sua conta'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                  placeholder="Seu nome"
                  required={!isLogin}
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                placeholder="admin@hitgrill.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)',
              }}
              data-testid="login-submit-button"
            >
              {loading ? 'Aguarde...' : (isLogin ? 'ENTRAR' : 'CADASTRAR')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-gray-500">ou</span>
          </div>

          <div className="mt-6 text-center">
            <span className="text-gray-600">
              {isLogin ? 'Não tem conta? ' : 'Já tem conta? '}
            </span>
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-orange-500 hover:text-orange-600 font-semibold"
            >
              {isLogin ? 'Cadastre-se' : 'Faça login'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
